import { Insight, InsightsKey } from "../../../api/aiInsights";
import { createEntityAdapter, createSlice, EntityState } from "@reduxjs/toolkit";
import { fetchInsightsThunk, generateInsightsThunk } from "./aiInsightsThunk";


export interface AIInsightExtended extends Insight {
    loadingStatus: 'loading' | 'succeeded' | 'failed';
}

interface InsightConfig {
    insight_key: InsightsKey;
    question_text: string;
    category: string;
}


interface AIInsightsState {
    caseId?: string;
    configs: EntityState<InsightConfig, InsightsKey>;
    insights: EntityState<AIInsightExtended, InsightsKey>;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
}

export const aiInsightsAdapter = createEntityAdapter<AIInsightExtended, InsightsKey>({
    selectId: (insight) => insight.insight_key
})

export const insightsConfigAdapter = createEntityAdapter<InsightConfig, InsightsKey>({
    selectId: (insightConfig) => insightConfig.insight_key
})

const DEFAULT_CONFIGS = [
    {
        insight_key: InsightsKey.KYC_DOCS_MANIPULATION,
        question_text: "Are there any signs of manipulations in KYC documents?",
        category: "KYC Analysis"
    },
    {
        insight_key: InsightsKey.CREDIT_BEHAVIOUR,
        question_text: "Fetch the pre-login and latest credit reports and share insights around credit behaviour of the borrower.",
        category: "Credit Report Analysis"
    }
]

const initialState: AIInsightsState = {
    configs: insightsConfigAdapter.getInitialState(
        insightsConfigAdapter.setAll(insightsConfigAdapter.getInitialState(), DEFAULT_CONFIGS)
    ),
    insights: aiInsightsAdapter.getInitialState(),
    status: 'idle'
}

const aiInsightsSlice = createSlice({
    name: "aiInsights",
    initialState: initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchInsightsThunk.pending, (state, action) => {
            if(action.meta.arg.insightKeys){
                action.meta.arg.insightKeys.forEach((insightKey) => {
                    const existingInsight = state.insights.entities[insightKey];
                    existingInsight.loadingStatus = 'loading';
                    aiInsightsAdapter.upsertOne(state.insights, existingInsight);
                })
            } else {
                state.status = 'loading';
                state.caseId = action.meta.arg.caseId;
            }
        }).addCase(fetchInsightsThunk.rejected, (state, action) => {
            if(action.meta.arg.insightKeys){
                action.meta.arg.insightKeys.forEach((insightKey) => {
                    const existingInsight = state.insights.entities[insightKey];
                    existingInsight.loadingStatus = 'failed';
                    aiInsightsAdapter.upsertOne(state.insights, existingInsight);
                })
            } else {
                state.status = 'failed';
            }
        }).addCase(fetchInsightsThunk.fulfilled, (state, action) => {
            const extendedInsights: AIInsightExtended[] = action.payload.insights.map(
                insight => ({
                    ...insight,
                    loadingStatus: 'succeeded'
                })
            )
            aiInsightsAdapter.upsertMany(state.insights, extendedInsights);
            state.status = 'succeeded';
        }).addCase(generateInsightsThunk.pending, (state, action) => {
            if(action.meta.arg.insightKeys){
                action.meta.arg.insightKeys.forEach((insightKey) => {
                    const existingInsight = state.insights.entities[insightKey];
                    existingInsight.loadingStatus = 'loading';
                    aiInsightsAdapter.upsertOne(state.insights, existingInsight);
                })
            } else {
                state.status = 'loading';
                state.caseId = action.meta.arg.caseId;
            }
        }).addCase(generateInsightsThunk.rejected, (state, action) => {
            if(action.meta.arg.insightKeys){
                action.meta.arg.insightKeys.forEach((insightKey) => {
                    const existingInsight = state.insights.entities[insightKey];
                    existingInsight.loadingStatus = 'failed';
                    aiInsightsAdapter.upsertOne(state.insights, existingInsight);
                })
            } else {
                state.status = 'failed';
            }
        }).addCase(generateInsightsThunk.fulfilled, (state, action) => {
            const extendedInsights: AIInsightExtended[] = action.payload.insights.map(
                insight => ({
                    ...insight,
                    loadingStatus: 'succeeded'
                })
            )
            aiInsightsAdapter.upsertMany(state.insights, extendedInsights);
            state.status = 'succeeded';
        })

    }
})

export const aiInsightsReducer = aiInsightsSlice.reducer;